import type {
  AutoSuggestPlace,
  Condition,
  DirectorySpecialtyOption,
  FetchSmartSuggestion,
  LocationServiceAdapter,
  NearestPlace,
  ServiceLine,
} from '@centivo/provider-directory-components'

import { client } from 'src/api'
import { getLocationServiceUrl } from 'src/context/functions/getAppConfig'

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '')

interface NearestPlaceResponse {
  place_id?: string
  addressComponents?: {
    postal_code?: string
  }
}

/**
 * Bridges oe-portal's location clients to the library `LocationServiceAdapter`.
 * Autocomplete reuses the shared `client.getAutoSuggestPlaces`; nearest-place
 * lookup (only used for geolocation prefill) hits the location service directly.
 */
export function createLocationServiceAdapter(): LocationServiceAdapter {
  const baseUrl = stripTrailingSlash(getLocationServiceUrl())

  return {
    getAutoSuggestPlaces: async (input): Promise<AutoSuggestPlace[]> => {
      const predictions = await client.getAutoSuggestPlaces(input)
      return (predictions ?? []) as AutoSuggestPlace[]
    },
    getNearestPlaceIdByGeometry: async (
      lat,
      long,
      { signal } = {}
    ): Promise<NearestPlace> => {
      const url =
        `${baseUrl}/getNearestPlaceIdByGeometry` +
        `?lat=${encodeURIComponent(String(lat))}` +
        `&long=${encodeURIComponent(String(long))}`
      const response = await fetch(url, {
        signal,
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        throw new Error(`getNearestPlaceIdByGeometry ${response.status}`)
      }
      const data: NearestPlaceResponse = await response.json()
      return {
        place_id: data.place_id ?? '',
        postal_code: data.addressComponents?.postal_code ?? '',
      }
    },
  }
}

interface RawServiceLine {
  name: string
  codes: string[]
  isOeDoctor?: boolean
  isOeFacility?: boolean
}

interface RawServiceLinesResponse {
  meta?: unknown
  data?: RawServiceLine[]
}

const serviceLinesCacheByBaseUrl = new Map<string, ServiceLine[]>()
const serviceLinesInflightByBaseUrl = new Map<string, Promise<ServiceLine[]>>()

function loadAllServiceLines(
  baseUrl: string,
  signal?: AbortSignal
): Promise<ServiceLine[]> {
  const cached = serviceLinesCacheByBaseUrl.get(baseUrl)
  if (cached) return Promise.resolve(cached)

  const inflight = serviceLinesInflightByBaseUrl.get(baseUrl)
  if (inflight) return inflight

  const request = fetch(`${baseUrl}/masterlist/serviceLines`, {
    signal,
    headers: { Accept: 'application/json' },
  })
    .then(async response => {
      if (!response.ok) {
        throw new Error(
          `masterlist/serviceLines failed: ${response.status} ${response.statusText}`
        )
      }
      const payload = (await response.json()) as RawServiceLinesResponse
      const serviceLines = (payload.data ?? [])
        .filter(sl => sl.name && sl.codes?.length)
        .filter(sl => sl.isOeDoctor || sl.isOeFacility)
        .map<ServiceLine>(sl => ({
          id: `${sl.codes.join(',')}:${sl.name}`,
          label: sl.name,
          isOeDoctor: sl.isOeDoctor,
          isOeFacility: sl.isOeFacility,
        }))
      const list = Array.from(
        new Map(
          serviceLines.map(serviceLine => [serviceLine.id, serviceLine])
        ).values()
      )
      serviceLinesCacheByBaseUrl.set(baseUrl, list)
      return list
    })
    .finally(() => {
      serviceLinesInflightByBaseUrl.delete(baseUrl)
    })

  serviceLinesInflightByBaseUrl.set(baseUrl, request)
  return request
}

export function createServiceLinesFetcher(
  providerApiUrl: string
): (query: string, opts?: { signal?: AbortSignal }) => Promise<ServiceLine[]> {
  const baseUrl = stripTrailingSlash(providerApiUrl)

  return async (query, opts) => {
    const all = await loadAllServiceLines(baseUrl, opts?.signal)
    const normalized = query.toLowerCase()
    return all
      .filter(sl => sl.label.toLowerCase().includes(normalized))
      .slice(0, 20)
  }
}

interface RawCondition {
  Condition?: string
  Aliases?: string[]
  ServiceLines?: string[]
  SpecialtyCodes?: string[]
}

const conditionsCacheByBaseUrl = new Map<string, Condition[]>()
const conditionsInflightByBaseUrl = new Map<string, Promise<Condition[]>>()

function mapRawCondition(raw: RawCondition): Condition | null {
  const label = raw.Condition?.trim()
  if (!label) return null
  return {
    id: label,
    label,
    aliases: (raw.Aliases ?? []).filter(Boolean),
    serviceLines: (raw.ServiceLines ?? []).filter(Boolean),
    specialtyCodes: (raw.SpecialtyCodes ?? []).filter(Boolean),
  }
}

function loadAllConditions(
  baseUrl: string,
  signal?: AbortSignal
): Promise<Condition[]> {
  const cached = conditionsCacheByBaseUrl.get(baseUrl)
  if (cached) return Promise.resolve(cached)

  const inflight = conditionsInflightByBaseUrl.get(baseUrl)
  if (inflight) return inflight

  const request = fetch(`${baseUrl}/masterlist/conditions`, {
    signal,
    headers: { Accept: 'application/json' },
  })
    .then(async response => {
      if (!response.ok) {
        throw new Error(
          `getConditions failed: ${response.status} ${response.statusText}`
        )
      }
      const raw = (await response.json()) as RawCondition[]
      const list = raw
        .map(mapRawCondition)
        .filter((c): c is Condition => c !== null)
      conditionsCacheByBaseUrl.set(baseUrl, list)
      return list
    })
    .finally(() => {
      conditionsInflightByBaseUrl.delete(baseUrl)
    })

  conditionsInflightByBaseUrl.set(baseUrl, request)
  return request
}

const normalize = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()

export function createConditionsFetcher(
  providerApiUrl: string
): (query: string, opts?: { signal?: AbortSignal }) => Promise<Condition[]> {
  const baseUrl = stripTrailingSlash(providerApiUrl)

  return async (query, opts) => {
    const all = await loadAllConditions(baseUrl, opts?.signal)
    const q = normalize(query)
    if (q.length < 2) return []
    const scored = all.flatMap(condition => {
      const labelMatch = normalize(condition.label)
      if (labelMatch.startsWith(q)) return [{ condition, score: 100 }]
      if (labelMatch.includes(q)) return [{ condition, score: 80 }]
      const aliasHit = condition.aliases.find(a => normalize(a).includes(q))
      if (aliasHit) {
        const score = normalize(aliasHit).startsWith(q) ? 60 : 40
        return [{ condition, score }]
      }
      return []
    })
    return scored
      .sort(
        (a, b) =>
          b.score - a.score ||
          a.condition.label.localeCompare(b.condition.label)
      )
      .slice(0, 10)
      .map(s => s.condition)
  }
}

export function createSmartSuggestionFetcher(): FetchSmartSuggestion {
  return async (query, opts) => {
    if (opts?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
    const result = await client.directorySmartSuggestion(query)
    if (opts?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
    return result
  }
}

/** Languages the directory can filter on. No runtime source exists yet. */
export const DIRECTORY_LANGUAGES_LIST = ['English', 'Spanish', 'Mandarin']

/**
 * Full OE specialty list mapped to the library's `DirectorySpecialtyOption`
 * shape. Used as the fallback filter list before a taxonomy-scoped search
 * returns its own `specialtyFilters`.
 */
export async function fetchDirectorySpecialties(): Promise<
  DirectorySpecialtyOption[]
> {
  const specialties = await client.getSpecialties()
  const byCode = new Map<string, DirectorySpecialtyOption>()
  specialties.forEach(specialty => {
    if (specialty.code && !byCode.has(specialty.code)) {
      byCode.set(specialty.code, {
        code: specialty.code,
        name: specialty.specialty,
      })
    }
  })
  return Array.from(byCode.values())
}
